import React, { useState, useMemo, useCallback, useContext, useRef } from 'react';

export type EditPageLoadingContextProviderProps = {
	nativeEditorComponentsPreloader: () => Promise<void>;
	children?: React.ReactNode;
};

export type LoadEditorArgs = Partial<EditLoadingContextType>;
export type EditLoadingContextType = {
	contentId: string;
	spaceKey: string | undefined;
	redirectUrl: string | null;
	contentType: string | undefined | null;
};
export type EditPageLoadingActionsContextType = {
	loadEditor: (args: LoadEditorArgs) => void;
	nativeEditorComponentsPreloader: () => Promise<void>;
};

export const defaultEditLoadingValues = {
	contentId: '',
	spaceKey: undefined,
	redirectUrl: null,
	contentType: null,
};

export const EditPageLoadingContext = React.createContext<EditLoadingContextType>({
	...defaultEditLoadingValues,
});

export const EditPageLoadingActionsContext = React.createContext<EditPageLoadingActionsContextType>(
	{
		loadEditor: () => {},
		nativeEditorComponentsPreloader: () => Promise.resolve(),
	},
);

export function EditPageLoadingContextProvider({
	nativeEditorComponentsPreloader,
	children,
}: EditPageLoadingContextProviderProps) {
	const [editLoadingValues, setEditLoadingValues] =
		useState<EditLoadingContextType>(defaultEditLoadingValues);

	const ref = useRef(() => Promise.resolve());
	ref.current = nativeEditorComponentsPreloader;
	const stableNativeEditorComponentsPreloader = useCallback(() => ref.current?.(), []);

	const actionsContextValue = useMemo(
		() => ({
			loadEditor: (newValues: LoadEditorArgs) =>
				setEditLoadingValues((oldState) => ({ ...oldState, ...newValues })),
			nativeEditorComponentsPreloader: stableNativeEditorComponentsPreloader,
		}),
		[setEditLoadingValues, stableNativeEditorComponentsPreloader],
	);

	const contextValue = useMemo(
		() => ({
			...editLoadingValues,
		}),
		[editLoadingValues],
	);

	return (
		<EditPageLoadingContext.Provider value={contextValue}>
			<EditPageLoadingActionsContext.Provider value={actionsContextValue}>
				{children}
			</EditPageLoadingActionsContext.Provider>
		</EditPageLoadingContext.Provider>
	);
}

export const useEditPageLoadingValues = (): EditLoadingContextType => {
	return useContext(EditPageLoadingContext);
};
export const useEditPageLoadingActions = (): EditPageLoadingActionsContextType => {
	return useContext(EditPageLoadingActionsContext);
};
